import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Entity } from '../entity/entity';
import { IecTask } from '../tasks/iec-task';
import { TaskResponse } from '../tasks/task-response';
import { PersonLogin, RoleName } from '../person/person-login';
import { EntityService } from '../services/entity-service';
import { IecTaskService } from '../services/iec-task-service';
import { PersonLoginService } from '../services/person-login-service';
import { TaskResponseService } from '../services/task-response-service';
import { StringUtils } from '../utils/string-utils';
import { DatePipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-sn-bypass-task-response',
  imports: [MatButton, MatIcon, MatProgressSpinner, DatePipe, FormsModule],
  templateUrl: './sn-bypass-task-response.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './sn-bypass-task-response.component.css',
})
export class SnBypassTaskResponseComponent implements OnInit, OnChanges {
  readonly taskIdParam = input.required<string>({ alias: 'taskId' });
  private readonly taskId = signal<number>(undefined!);
  private personLogin!: PersonLogin;
  private readonly iecTaskService = inject(IecTaskService);
  private readonly personLoginService = inject(PersonLoginService);
  private readonly taskResponseService = inject(TaskResponseService);
  private readonly entityService = inject(EntityService);
  private readonly router = inject(Router);
  readonly task = signal<IecTask>(undefined!);
  readonly isBusy = signal<boolean>(false);
  readonly errorMessage = signal<string>(undefined!);
  readonly taskMessage = signal<string>(undefined!);
  readonly entity = signal<Entity>(undefined!);
  readonly assignedTo = signal<string>(undefined!);
  notes: string = '';

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']).then(() => (this.isBusy.set(false)));
    } else {
      this.personLogin = this.personLoginService.getCachedLogin()!;
      this.loadTask();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['taskIdParam'].firstChange) {
      this.loadTask();
    }
  }

  private loadTask(): void {
    const id: string = this.taskIdParam();
    if (id && !isNaN(+id)) {
      this.taskId.set(+id);
      this.loadTaskDetails();
    } else {
      this.taskMessage.set('Cannot find task information with an ID of ' + id);
    }
  }

  canCloseTask(): boolean {
    if (!this.task() || !this.personLogin) {
      return false;
    }
    return this.isCloseAccessAllowed();
  }

  closeTask(isApproved: boolean): void {
    if (!this.task()) {
      return;
    }
    if (!this.isCloseAccessAllowed()) {
      this.taskMessage.set('You do not have permission to close this task');
      return;
    }
    if (isApproved && this.task().name === 'SN Bypass Rejection Follow-up' && StringUtils.isBlank(this.notes)) {
      this.errorMessage.set('Please enter notes describing the information you are providing before approving.');
      return;
    }
    if (!isApproved && this.task().name === 'SN Bypass Rejection Follow-up' && StringUtils.isNotBlank(this.notes)) {
      const confirmed = window.confirm('You have entered notes. Notes are only saved on Approve and will be discarded if you Deny. Continue?');
      if (!confirmed) {
        return;
      }
    }
    this.isBusy.set(true);
    const response = new TaskResponse();
    response.taskId = this.taskId();
    response.responder = this.personLogin;
    response.approved = isApproved;
    response.additionalData = this.notesPassedThroughOnAction(isApproved) ? this.notes : '';
    this.taskResponseService.closeTask(response).subscribe({
      next: () => {
        this.isBusy.set(false);
        this.loadTaskDetails();
      },
      error: (error) => this.handleError(error),
    });
  }

  get notesLabel(): string {
    if (this.task()?.name === 'SN Bypass Rejection Follow-up') {
      return 'Notes (required to Approve, sent to Parts Engineering):';
    }
    return 'Notes (added to External Notes on Approve, sent to sales rep on Deny):';
  }

  get approveLabel(): string {
    if (this.task()?.name === 'SN Bypass Rejection Follow-up') {
      return 'Submit Info';
    }
    return 'Approve Bypass';
  }

  get denyLabel(): string {
    if (this.task()?.name === 'SN Bypass Rejection Follow-up') {
      return 'Place on Hold';
    }
    return 'Request Info from Rep';
  }

  private notesPassedThroughOnAction(isApproved: boolean): boolean {
    if (this.task()?.name === 'SN Bypass Rejection Follow-up') {
      return isApproved;
    }
    return true;
  }

  getEntityUrl(): string {
    if (this.entity()) {
      const pageName: string = this.entity().id.entityName === 'QUOTE' ? 'quotes' : 'orders';
      return pageName + '/' + this.entity().id.entityId + '/' + this.entity().id.entityRevision;
    }
    return '';
  }

  private loadTaskDetails(): void {
    this.isBusy.set(true);
    this.initializeTaskData().then(
      () => (this.isBusy.set(false)),
      (error: HttpErrorResponse) => this.handleError(error),
    );
  }

  private async initializeTaskData(): Promise<void> {
    this.task.set(await firstValueFrom<IecTask>(this.iecTaskService.getTask(this.taskId())));
    if (!this.task()) {
      this.taskMessage.set('No task was found with ID ' + this.taskId());
      this.entity.set(undefined!);
    } else if (this.task().endTime) {
      this.taskMessage.set('This task has been closed, you can close this window or use the links above to go to a different screen');
      this.task.set(undefined!);
      this.entity.set(undefined!);
    } else {
      this.assignedTo.set(await firstValueFrom<string>(this.iecTaskService.getAssignedTo(this.task().assignee)));
      if (StringUtils.isNotBlank(this.task().entityId)) {
        const entityPattern = new RegExp(/^((QT|MR|SR|R)\d{6,7})$/);
        if (entityPattern.test(this.task().entityId)) {
          const entityName: string = this.task().entityId.startsWith('QT') ? 'QUOTE' : 'CONTRACT';
          this.entity.set(await firstValueFrom<Entity>(this.entityService.getEntity(entityName, this.task().entityId, this.task().entityRevision)));
        }
      }
    }
  }

  private handleError(error: HttpErrorResponse): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
  }

  private isCloseAccessAllowed(): boolean {
    if (!this.task()) {
      return true;
    }
    if (this.personLoginService.hasRole(RoleName.IECIT)) {
      return true;
    }
    return this.personLogin.token === this.task().assignee
      || this.personLogin.groups.filter((group) => group.token === this.task().assignee).length > 0;
  }
}
